import { type FC, useState } from "react";
import Measure, { type ContentRect } from "react-measure";
import { useNavigate, useParams } from "react-router";
import { useDebouncedCallback } from "use-debounce";

import { LoadingSpinner } from "widgets";

import styleLayout from "../core/styles/PageLayout.module.scss";

import ButtonHeader from "../core/ButtonHeader.js";
import BlastPageHeader from "./BlastPageHeader.js";
import { QueryAnchoredHitsVis } from "./QueryAnchoredHitsVis.js";
import { type BlastHit, type BlastHsp, type BlastJob, type JobResult } from "@/graphql.ts";
import { BLAST_SUB_VIEW, VIEW } from "@/settings/settings.ts";

interface IBlastResultSummaryViewProps {
  job: BlastJob;
  isLoading: boolean;
  jobResult: JobResult;
}

const DEBOUNCE_TIMER = 100;

const BlastResultSummaryView: FC<IBlastResultSummaryViewProps> = (props) => {
  const [viewWidth, setViewWidth] = useState<number>();
  const navigate = useNavigate();

  const debouncedViewWidthChanged = useDebouncedCallback((contentRect: ContentRect) => {
    setViewWidth(contentRect.bounds?.width);
  }, DEBOUNCE_TIMER);

  const { jobId } = useParams<"jobId">();

  const goToAlignment = (hit: BlastHit, hsp: BlastHsp) => {
    navigate(
      `/job/${jobId}/results/${props.jobResult.id}/alignments${hit.rank ? `/${hit.rank}` : ""}${
        hsp.rank ? `/${hsp.rank}` : ""
      }`,
    );
  };

  return (
    <>
      <BlastPageHeader
        job={props.job}
        jobResult={props.jobResult}
        subtitle={"Graphic Summary"}
        actionWidgets={[
          <ButtonHeader
            viewType={VIEW.BLAST_JOB_VIEW.type}
            subViewType={BLAST_SUB_VIEW.SUMMARY}
            hideBlastBtn={true}
            hideExportBtn={true}
            key={"blast-result-summary-view-action-1"}
          />,
        ]}
        isLoading={props.isLoading}
      />
      <Measure bounds onResize={debouncedViewWidthChanged}>
        {({ measureRef }) => (
          <div ref={measureRef} className={styleLayout.page} style={{ backgroundColor: "white" }}>
            {!!viewWidth &&
              (!props.jobResult || props.isLoading ? (
                <LoadingSpinner inCenter />
              ) : (
                <QueryAnchoredHitsVis
                  {...QueryAnchoredHitsVis.getPropsWithDefaultBoxModel({
                    boxModel: {
                      width: viewWidth - 20, // account for possible vertical scroll bar
                      padding: {
                        top: 10,
                        right: 20,
                        bottom: 10,
                        left: 20,
                      },
                    },
                    onHitClick: goToAlignment,
                    hits: props.jobResult.blastHits,
                    query: props.jobResult.query,
                  })}
                />
              ))}
          </div>
        )}
      </Measure>
    </>
  );
};

export { BlastResultSummaryView };
